// src/pages/Dashboard.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDashboardItems } from '../services/AppService';
import AboutLogo from '../assets/images/UserDashboard/AboutUS.png';
import LogoutLogo from '../assets/images/UserDashboard/logOutBg.png';
import CardComponent from '../components/CardComponent';
import { useAuth } from '../context/AuthContext';
import { getEmployeeAccess, logoutUser } from '../services/authService';
import { useTranslation } from 'react-i18next';
import AuthLoader from '../components/Login/AuthLoader';
import PageFade from '../components/Login/PageFade';

const Dashboard = () => {
  const { t } = useTranslation();

  const [cards, setCards] = useState([]);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const { user, refreshToken, logout } = useAuth();

  const navigate = useNavigate();

  const MIN_LOGOUT_LOADING_TIME = 2000; // 2 seconds
  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));


  // Logout handler
  const handleLogout = async () => {
    setLogoutLoading(true);

    try {
      await Promise.all([
        logoutUser(refreshToken),        // API logout
        delay(MIN_LOGOUT_LOADING_TIME),  // compulsory loader
      ]);
    } catch (err) {
      console.error("Logout API failed", err);
      // Still proceed with local logout
    } finally {
      logout(); // clear auth state
      navigate("/smartcity/login", { replace: true });
      setLogoutLoading(false);
    }
  };


  useEffect(() => {
    // Fetch cards from API
    const fetchData = async () => {
      try {
        const data = await getDashboardItems();
        setCards(data);
      } catch (error) {
        console.error('Error fetching dashboard items', error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let accessData = null;

        // Only fetch specific menu access if the user is an employee (USER role)
        // Admins usually see everything
        if (user?.role === 'USER') {
          accessData = await getEmployeeAccess(user.userId);
        }

        console.log("Department :", user?.department);
        console.log("Designation :", user?.designation);
        const filteredCards = await getDashboardItems(accessData, user);
        setCards(filteredCards);
      } catch (error) {
        console.error('Error fetching dashboard items', error);
      }
    }

    if (user?.userId) {
      fetchData();
    }
  }, [user]);

  return (
    <PageFade>
      {logoutLoading && <AuthLoader text="Signing you out securely..." />}

      {/* Dashboard Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 justify-items-center">
        {cards.map((card, index) => (
          <div key={index} className="relative w-full flex justify-center">
            <div
              className={`w-full flex justify-center transition-all duration-300 ${
                !card.hasAccess ? 'opacity-50 grayscale pointer-events-none' : ''
              }`}
            >
              <CardComponent
                imgSrc={card.image}
                title={card.title}
                translationKey={card.translationKey}
                onClick={() => card.hasAccess && navigate(card.link || '/')}
              />
            </div>
            {!card.hasAccess && (
              <div className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-red-50 border border-red-200 text-red-600 text-[10px] md:text-xs font-semibold px-2 py-0.5 rounded shadow-sm z-10 pointer-events-none">
                {t('dashboard.noAccess', 'No Access')}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* About Us & Logout */}
      <div className="mt-6">
        <div>
          <div className='flex justify-center gap-3 mt-3'>
            <div
              className="flex flex-col items-center shadow-[2px_2px_7px_#8acafb] cursor-pointer justify-center bg-white shadow-md rounded-xl p-2 cursor-pointer hover:shadow-lg transition-all duration-300 w-52 h-32"
              onClick={() => navigate('/smartcity/about-us')}
            >
              <img src={AboutLogo} alt="About" className="w-12 h-12 mb-2" />
              <p className="font-medium text-center">{t('dashboard.aboutUs') || 'About Us'}</p>
            </div>
            <div
              className="flex flex-col items-center shadow-[2px_2px_7px_#8acafb] cursor-pointer justify-center bg-white shadow-md rounded-xl p-2 cursor-pointer hover:shadow-lg transition-all duration-300 w-52 h-32"
              onClick={handleLogout}
            >
              <img src={LogoutLogo} alt="Logout" className="w-12 h-12 mb-2" />
              <p className="font-medium text-center">
                {logoutLoading ? "Signing out..." : (t('dashboard.logout') || 'Logout')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </PageFade>
  );
};

export default Dashboard;
